/**
 * LeadResponsesDrawer — Avika lead responses slide-over (Task A7, 2026-06-10).
 *
 * Opened from the "Responses" cell in the Lead Triage Dashboard. On open it
 * fetches GET /api/v1/leads/:id/avika-detail and renders:
 *   - the WhatsApp Q&A transcript as assistant / user bubbles
 *   - the full Avika CRM field block
 *   - the B2B block when model === 'b2b'
 *   - the score model + per-field contribution breakdown
 *
 * Reuses the dashboard's modal-overlay pattern (fixed inset, backdrop click to
 * close) styled as a right-hand slide-over panel. Tailwind classes only.
 */

import { useEffect, useState } from 'react';
import { TierChip } from './TierChip';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';

// ─── Contract (mirrors GET /api/v1/leads/:id/avika-detail) ────────────────────

interface TranscriptTurn {
  role: 'assistant' | 'user' | string;
  content: string;
  at?: string | null;
}

interface Contribution {
  label: string;
  points: number;
}

type AvikaCrm = Record<string, unknown>;

interface AvikaDetail {
  crm: AvikaCrm;
  transcript: TranscriptTurn[];
  score: number | null;
  tier: 'HOT' | 'WARM' | 'COLD' | null;
  model: 'end_customer' | 'no_price' | 'b2b' | null;
  contributions?: Contribution[] | Record<string, number> | null;
}

// CRM fields shown in the main (non-B2B) block, in render order.
const CRM_FIELDS: { key: string; label: string }[] = [
  { key: 'lead_name', label: 'Name' },
  { key: 'lead_product', label: 'Products' },
  { key: 'lead_location_city', label: 'City' },
  { key: 'lead_location_state', label: 'State' },
  { key: 'lead_application', label: 'Application' },
  { key: 'lead_capacity', label: 'Capacity' },
  { key: 'lead_capacity_source', label: 'Capacity source' },
  { key: 'lead_indicative_price_shared', label: 'Price shared' },
  { key: 'lead_indicative_price_range', label: 'Price range' },
  { key: 'continued_after_price', label: 'Continued after price' },
  { key: 'lead_timeline', label: 'Timeline' },
  { key: 'lead_out_of_budget', label: 'Out of budget' },
  { key: 'lead_boq_received', label: 'BoQ received' },
];

// B2B-only fields.
const B2B_FIELDS: { key: string; label: string }[] = [
  { key: 'b2b_business_type', label: 'Business type / trade' },
  { key: 'b2b_business_name', label: 'Firm name' },
  { key: 'b2b_gst', label: 'GSTIN' },
  { key: 'b2b_has_gst', label: 'Has GST' },
  { key: 'b2b_card_received', label: 'Card received' },
  { key: 'b2b_years_in_business', label: 'Years in business' },
  { key: 'b2b_has_showroom', label: 'Has showroom' },
  { key: 'b2b_channel_type', label: 'Channel type' },
  { key: 'b2b_coverage_area', label: 'Coverage area' },
  { key: 'b2b_expected_volume', label: 'Expected volume' },
  { key: 'b2b_existing_brands', label: 'Existing brands' },
];

function formatValue(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}

function FieldGrid({ fields, crm }: { fields: { key: string; label: string }[]; crm: AvikaCrm }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {fields.map((f) => (
        <div key={f.key} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-gray-400">{f.label}</dt>
          <dd className="text-gray-900 break-words">{formatValue(crm[f.key])}</dd>
        </div>
      ))}
    </dl>
  );
}

function normaliseContributions(
  c: AvikaDetail['contributions'],
): Contribution[] {
  if (!c) return [];
  if (Array.isArray(c)) return c;
  return Object.entries(c).map(([label, points]) => ({ label, points: Number(points) }));
}

export function LeadResponsesDrawer({
  leadId,
  onClose,
}: {
  leadId: string | null;
  onClose: () => void;
}) {
  const [detail, setDetail] = useState<AvikaDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!leadId) {
      setDetail(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    fetch(`/api/v1/leads/${leadId}/avika-detail`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        return (json?.data ?? json) as AvikaDetail;
      })
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [leadId]);

  if (!leadId) return null;

  const contributions = normaliseContributions(detail?.contributions);
  const isB2b = detail?.model === 'b2b';

  return (
    <Modal open side="right" onClose={onClose} ariaLabel="Lead responses" className="flex flex-col !max-w-xl !overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Avika responses</h2>
            <p className="text-xs text-gray-500">Full WhatsApp Q&amp;A + lead score breakdown</p>
          </div>
          <RequestCloseButton
            className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            aria-label="Close"
          >×</RequestCloseButton>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {loading && (
            <div className="text-sm text-gray-500">Loading responses…</div>
          )}
          {error && !loading && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
              Could not load responses: {error}
            </div>
          )}

          {detail && !loading && (
            <>
              {/* Score summary */}
              <section className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-3xl font-extrabold text-gray-900 leading-none">
                    {detail.score ?? '—'}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mt-1">Score</div>
                </div>
                <div className="flex flex-col gap-1">
                  <TierChip
                    tier={detail.tier}
                    isB2bPriority={Boolean(detail.crm?.lead_is_b2b_priority)}
                    isOutOfBudget={Boolean(detail.crm?.lead_out_of_budget)}
                  />
                  {detail.model && (
                    <span className="text-xs text-gray-500">
                      Scoring model: <span className="font-medium text-gray-700">{detail.model}</span>
                    </span>
                  )}
                </div>
              </section>

              {/* Transcript */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  WhatsApp transcript
                </h3>
                {detail.transcript?.length ? (
                  <div className="space-y-2">
                    {detail.transcript.map((t, i) => {
                      const isUser = t.role === 'user';
                      return (
                        <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                              isUser
                                ? 'bg-blue-600 text-white rounded-br-sm'
                                : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                            }`}
                          >
                            <div className="whitespace-pre-wrap break-words">{t.content}</div>
                            {t.at && (
                              <div className={`text-[10px] mt-1 ${isUser ? 'text-blue-100' : 'text-gray-400'}`}>
                                {new Date(t.at).toLocaleString()}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-sm text-gray-400">No transcript captured.</div>
                )}
              </section>

              {/* CRM block */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Captured fields
                </h3>
                <FieldGrid fields={CRM_FIELDS} crm={detail.crm ?? {}} />
              </section>

              {/* B2B block (only when model === 'b2b') */}
              {isB2b && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-600 mb-2">
                    B2B / dealer details
                  </h3>
                  <FieldGrid fields={B2B_FIELDS} crm={detail.crm ?? {}} />
                </section>
              )}

              {/* Score contribution breakdown */}
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  Score breakdown
                </h3>
                {contributions.length ? (
                  <ul className="space-y-1">
                    {contributions.map((c, i) => (
                      <li key={i} className="flex items-center justify-between text-sm border-b border-gray-100 py-1">
                        <span className="text-gray-700">{c.label}</span>
                        <span className={`font-semibold ${c.points >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                          {c.points >= 0 ? '+' : ''}{c.points}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-sm text-gray-400">No per-field breakdown available.</div>
                )}
              </section>
            </>
          )}
        </div>
    </Modal>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}

export default LeadResponsesDrawer;
